import { useEffect, useRef, useState } from "react";
import type { Post } from "../lib/wall";
import { REACTIONS, REACTION_LABEL, type ReactionCounts } from "../lib/reactions";

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

function ago(at: number): string {
  const d = Date.now() - at;
  if (d < MINUTE) return "just now";
  if (d < HOUR) return `${Math.round(d / MINUTE)}m ago`;
  if (d < DAY) return `${Math.round(d / HOUR)}h ago`;
  return `${Math.round(d / DAY)}d ago`;
}

interface Props {
  posts: Post[];
  landedId: string | null;
  reactions: ReactionCounts;
  mine: Record<string, string[]>;
  onReact: (postId: string, emoji: string) => void;
}

export default function Wall({ posts, landedId, reactions, mine, onReact }: Props) {
  const landed = useRef<HTMLLIElement | null>(null);

  /** Ids already on screen. Everything in the first batch counts as seen, so a
   *  page load does not animate the entire wall. */
  const seen = useRef<Set<string> | null>(null);
  const [arrived, setArrived] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (seen.current === null) {
      seen.current = new Set(posts.map((p) => p.id));
      return;
    }
    const fresh = posts.filter((p) => !seen.current!.has(p.id)).map((p) => p.id);
    if (fresh.length === 0) return;
    fresh.forEach((id) => seen.current!.add(id));
    setArrived(new Set(fresh));
    // Drop the marker after the animation so a re-render does not replay it.
    const t = window.setTimeout(() => setArrived(new Set()), 1200);
    return () => window.clearTimeout(t);
  }, [posts]);

  useEffect(() => {
    if (!landedId || !landed.current) return;
    landed.current.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [landedId]);

  // Relative stamps go stale on a page left open; the poll would refresh them
  // only when someone else posts.
  const [, tick] = useState(0);
  useEffect(() => {
    const t = window.setInterval(() => tick((n) => n + 1), MINUTE);
    return () => window.clearInterval(t);
  }, []);

  return (
    <section className="wall" aria-labelledby="wall-h">
      <div className="section-head">
        <h2 className="eyebrow" id="wall-h">
          The wall
        </h2>
        <p className="tally">
          <span className="tally__n">{posts.length.toLocaleString()}</span>
          <span className="tally__u">posts</span>
        </p>
      </div>

      <ol className="courses">
        {posts.map((p, i) => (
          <li
            key={p.id}
            ref={p.id === landedId ? landed : undefined}
            // Newest overlaps the one beneath it, like shields in a line.
            style={{ zIndex: posts.length - i }}
            className={
              "tile" +
              (p.mine ? " tile--mine" : "") +
              (p.id === landedId ? " tile--landed" : "") +
              (arrived.has(p.id) && p.id !== landedId ? " tile--arrived" : "")
            }
          >
            <p className="tile__msg">{p.text}</p>
            <p className="tile__meta">
              <span className="tile__who">{p.who}</span>
              <span className="tile__dot" aria-hidden="true">
                /
              </span>
              <time dateTime={new Date(p.at).toISOString()}>{ago(p.at)}</time>
            </p>

            <ul className="reacts" aria-label={`Reactions to the post by ${p.who}`}>
              {REACTIONS.map((emoji) => {
                const count = reactions[p.id]?.[emoji] ?? 0;
                const pressed = (mine[p.id] ?? []).includes(emoji);
                return (
                  <li key={emoji}>
                    <button
                      type="button"
                      className="react"
                      aria-pressed={pressed}
                      // The glyph alone reads as "thumbs up sign"; this says what
                      // pressing it means and what it currently stands at.
                      aria-label={`${REACTION_LABEL[emoji]} — ${count} ${count === 1 ? "person" : "people"}`}
                      onClick={() => onReact(p.id, emoji)}
                    >
                      <span className="react__glyph" aria-hidden="true">
                        {emoji}
                      </span>
                      <span className="react__n" aria-hidden="true">
                        {count}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </li>
        ))}
      </ol>

      {posts.length === 0 && (
        <p className="wall-empty">No one has posted yet. Go first — the wall starts with you.</p>
      )}
    </section>
  );
}
